import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled, {keyframes, css} from 'styled-components';
import {Link, withRouter} from 'react-router-dom';
import jsonLogic from 'json-logic-js';

import {getVariables, parseAnswerValue} from '../../lib/json-logic-utils';
import {watchSheetRows} from '../../lib/sheet-row-watcher';
import {buildCatalogMap} from '../../lib/catalog';
import Answer, {getAnswerText, isCatalogQuestion, IMAGE_ANSWER_WIDTH} from '../Answer';
import Avatar from '../Avatar';
import Placeholder from '../Placeholder';
import TimeAgo from '../TimeAgo';
import VirtualizedList from '../VirtualizedList';
import WidgetHeader from '../WidgetHeader';
import {UserLink} from '../UserLink';
import {getQuestionDisplayLabel} from '../../screens/FormBuilderScreen/shared';

// How many sheets are watched closely. A dashboard can hold hundreds of sheets, nearly all of them finished, and
// new data arrives on the ones most recently worked in.
const MAX_WATCHED_SHEETS = 10;
// The most submissions we keep in memory. Older ones fall off the bottom as new ones arrive.
const MAX_SUBMISSIONS = 500;
// How long a newly arrived submission stays highlighted.
const HIGHLIGHT_DURATION = 10000;

const EMPTY_ARRAY = [];

function getSheetLastModified(sheet) {
  return sheet.dates ? sheet.dates.lastModified : 0;
}

function RecentSubmissionsWidget({
  id,
  widget,
  options,
  sheetsById,
  rows,
  questions,
  catalog,
  preview,
  credentials,
  filters,
  formulaDeletedQuestions,
  match: {params}
}) {
  // The question this widget lists answers for. It is kept in groupByQuestion, the field the API already has for
  // the question a widget is about.
  const questionId = widget.groupByQuestion;
  const question = questions[questionId];
  const sheetIds = useMemo(() => Object.keys(sheetsById || {}), [sheetsById]);
  // The sheets worth asking about on every check: a completed sheet takes no new submissions - the API turns a scale
  // away from one - and of the rest it is the ones worked in most recently that data arrives on.
  const watchedSheetIds = useMemo(() => Object.keys(sheetsById || {})
    .map(sheetId => sheetsById[sheetId])
    .filter(sheet => sheet && !sheet.completed)
    .sort((a, b) => getSheetLastModified(b) - getSheetLastModified(a))
    .slice(0, MAX_WATCHED_SHEETS)
    .map(sheet => sheet.id), [sheetsById]);
  // The widget's filter is re-parsed by our parent on every render, so hold on to it by value. Without this the
  // submission list would be rebuilt each render and the virtualized list would reset its measurements.
  const filterKey = widget.filter ? JSON.stringify(widget.filter) : '';
  const filter = useMemo(() => filterKey ? JSON.parse(filterKey) : null, [filterKey]);
  // Questions are keyed into a fresh map by the dashboard on every render, so they are read through a ref rather than
  // being made a dependency.
  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  // Submissions we have loaded ourselves, keyed by row id. Null until the first poll returns, at which point it takes
  // over from the rows handed down by the dashboard.
  const [liveRowsById, setLiveRowsById] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [failed, setFailed] = useState(false);
  // Set when the API turned a check away for something asking again will not fix. Checking stops rather than showing
  // "Reconnecting..." forever.
  const [stopped, setStopped] = useState(false);
  const knownRowIds = useRef(null);
  // The font the rows are drawn in, read off the list once it is on the page, so their text can be measured.
  const [font, setFont] = useState(null);
  const readFont = useCallback(node => {
    if (node) {
      const style = window.getComputedStyle(node);
      setFont(`${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`);
    }
  }, []);

  const toSubmission = useCallback((row, sheetId) => {
    if (!question) {
      return null;
    }

    const answers = (row.answers || []).filter(answer => answer.questionId === questionId);

    if (answers.length < 1) {
      // The question was not answered on this submission, so there is nothing to show for it.
      return null;
    }

    const sheet = sheetsById[row.sheetId || sheetId] || {};

    return {
      rowId: row.id,
      sheetId: row.sheetId || sheetId,
      sheetName: sheet.name,
      submitter: row.user || sheet.user,
      lastModified: row.dates ? row.dates.lastModified : null,
      // Values arrive as strings from the API but the dashboard's worker parses them, so normalise both to the same
      // shape. This keeps the widget's filter behaving the same before and after the first poll.
      answers: answers.map(answer => ({...answer, value: parseAnswerValue(answer.value, question.type)})),
      rowAnswers: row.answers || EMPTY_ARRAY
    };
  }, [questionId, question, sheetsById]);

  const matchesFilter = useCallback(submission => {
    if (!filter) {
      return true;
    }

    try {
      return Boolean(jsonLogic.apply(filter, getVariables(submission.rowAnswers, questionsRef.current)));
    }
    catch (e) {
      // A filter that references a deleted question throws. Leave the submission in rather than silently emptying the
      // widget - the header already warns about deleted questions.
      return true;
    }
  }, [filter]);

  // Submissions handed down by the dashboard. Used until our first poll returns.
  const initialSubmissions = useMemo(() => (rows || [])
    .map(row => toSubmission(row))
    .filter(Boolean)
    .filter(matchesFilter), [rows, toSubmission, matchesFilter]);

  const submissions = useMemo(() => {
    if (!liveRowsById) {
      return initialSubmissions.slice(0, MAX_SUBMISSIONS);
    }

    return Object.keys(liveRowsById)
      .map(rowId => liveRowsById[rowId])
      .filter(matchesFilter)
      .sort((a, b) => b.lastModified - a.lastModified)
      .slice(0, MAX_SUBMISSIONS);
  }, [liveRowsById, initialSubmissions, matchesFilter]);

  // Keep the latest seed rows available to the poll without making them a dependency of it.
  const initialSubmissionsRef = useRef(initialSubmissions);
  initialSubmissionsRef.current = initialSubmissions;

  // Watch the sheets for new rows and merge them into what we already have. Every Recent Submissions widget on the
  // page shares one watcher, so however many there are, the API is asked once per check.
  useEffect(() => {
    if (preview || !credentials || !question || sheetIds.length < 1) {
      return undefined;
    }

    const handleRows = pages => {
      const now = Date.now();
      // The first rows are everything there already was, so none of them count as arriving - otherwise the whole
      // list would light up on load.
      const first = knownRowIds.current === null;

      if (first) {
        knownRowIds.current = new Set(initialSubmissionsRef.current.map(submission => submission.rowId));
      }

      const polled = pages.reduce((map, {sheetId, content}) => {
        content.forEach(row => {
          const submission = toSubmission(row, sheetId);

          if (submission) {
            if (!knownRowIds.current.has(submission.rowId)) {
              knownRowIds.current.add(submission.rowId);

              if (!first) {
                submission.arrivedAt = now;
              }
            }

            map[submission.rowId] = submission;
          }
        });

        return map;
      }, {});

      // When a submission arrived travels with it, rather than in state of its own. The list only redraws its rows when
      // the submissions change, so a highlight set separately would not show until whatever next changed them - by
      // which time the highlighted row was usually the second one down.
      setLiveRowsById(previous => {
        const merged = {...(previous || toMap(initialSubmissionsRef.current))};

        Object.keys(polled).forEach(rowId => {
          const earlier = merged[rowId];
          // A submission fetched again while it is still highlighted - edited, say - stays highlighted from when it
          // first arrived.
          merged[rowId] = earlier && earlier.arrivedAt && !polled[rowId].arrivedAt
            ? {...polled[rowId], arrivedAt: earlier.arrivedAt}
            : polled[rowId];
        });

        const next = toMap(Object.keys(merged)
          .map(rowId => merged[rowId])
          .sort((a, b) => b.lastModified - a.lastModified)
          .slice(0, MAX_SUBMISSIONS));

        // Most polls return exactly what we already have. Keeping the same object then means the list is not
        // rebuilt and does not lose the reader's scroll position.
        return isSameSubmissions(previous, next) ? previous : next;
      });
    };

    const handleStatus = status => {
      setFailed(status.failed);
      setStopped(status.stopped);
      setLastUpdate(status.lastUpdate);
    };

    return watchSheetRows({sheetIds, watchedSheetIds, filters, credentials}, {onRows: handleRows, onStatus: handleStatus});
  }, [preview, credentials, question, sheetIds, watchedSheetIds, filters, toSubmission]);

  // Each row is laid out on its own, so the sheet and answer columns are given the width of their longest entry to start
  const columnWidths = useMemo(
    () => font && question ? measureColumns(submissions, question, catalog, font) : null,
    [font, submissions, question, catalog]
  );
  const columnStyle = columnWidths ? {
    '--sheet-width': `${columnWidths.sheet}px`,
    '--answer-width': `${columnWidths.answer}px`
  } : undefined;

  if (!question) {
    return (
      <React.Fragment>
        <WidgetHeader id={id} title={widget.title} options={preview ? null : options} preview={preview} />
        <Container>
          <Placeholder height="100%">The question this widget was built from no longer exists.</Placeholder>
        </Container>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <WidgetHeader
        id={id}
        title={widget.title}
        question={question}
        options={preview ? null : options}
        preview={preview}
        formulaDeletedQuestions={formulaDeletedQuestions}
      />
      <SubTitle>
        <QuestionText title={getQuestionDisplayLabel(question)}>{getQuestionDisplayLabel(question)}</QuestionText>
        {!preview && stopped && (
          <Status failed title="Updating Widget Data Failed. Reload the page to try again.">
            Live updates stopped
          </Status>
        )}
        {!preview && !stopped && lastUpdate && (
          <Status failed={failed}>
            {failed ? 'Reconnecting...' : <React.Fragment>Updated <TimeAgo date={lastUpdate} /></React.Fragment>}
          </Status>
        )}
      </SubTitle>
      {submissions.length < 1 ? (
        <Container>
          <Placeholder height="100%">No submissions have answered this question yet.</Placeholder>
        </Container>
      ) : (
        <React.Fragment>
          <HeaderRow style={columnStyle}>
            <SheetColumn>Sheet</SheetColumn>
            <AnswerColumn>Answer</AnswerColumn>
            <DateColumn>Submitted</DateColumn>
            <SubmitterColumn>Submitted by</SubmitterColumn>
          </HeaderRow>
          <ScrollContent ref={readFont} style={columnStyle}>
            {/* Held back until the columns are measured, so the rows' heights are measured at the widths they keep. */}
            {columnWidths && (
            <VirtualizedList measurable items={submissions} defaultHeight={56}>
              {(submission, index, measure) => {
                const sinceArrival = submission.arrivedAt ? Date.now() - submission.arrivedAt : null;
                const highlighted = sinceArrival !== null && sinceArrival < HIGHLIGHT_DURATION;

                // The list rebuilds its rows whenever the submissions change, which restarts the fade. Starting it
                // as far in as the submission has been here keeps it fading from when it arrived rather than from
                // whenever the list last redrew.
                return (
                  <SubmissionRow highlighted={highlighted} style={highlighted ? {animationDelay: `-${sinceArrival}ms`} : undefined}>
                    <SheetColumn title={submission.sheetName || 'Unnamed sheet'}>
                      {submission.sheetName || 'Unnamed sheet'}
                    </SheetColumn>
                    <AnswerColumn>
                      <Answer
                        question={question}
                        answers={submission.answers}
                        catalog={catalog}
                        onImageLoaded={measure}
                      />
                    </AnswerColumn>
                    <DateColumn>
                      {preview ? (
                        <TimeAgo date={submission.lastModified} />
                      ) : (
                        <Link to={`/sheet/${params.templateId}/${params.sheetIds}/data/${submission.rowId}`}>
                          <TimeAgo date={submission.lastModified} />
                        </Link>
                      )}
                    </DateColumn>
                    <SubmitterColumn>
                      {submission.submitter && (
                        <React.Fragment>
                          <Avatar user={submission.submitter} size="2rem" />
                          <SubmitterName>
                            {preview ? submission.submitter.userName : <UserLink user={submission.submitter} />}
                          </SubmitterName>
                        </React.Fragment>
                      )}
                    </SubmitterColumn>
                  </SubmissionRow>
                );
              }}
            </VirtualizedList>
            )}
          </ScrollContent>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

function isSameSubmissions(previous, next) {
  if (!previous) {
    return false;
  }

  const previousIds = Object.keys(previous);
  const nextIds = Object.keys(next);

  return previousIds.length === nextIds.length
    && nextIds.every(rowId => previous[rowId] && previous[rowId].lastModified === next[rowId].lastModified);
}

// Measured widths are rounded up and given a little extra, so text measured to the pixel does not end up truncated.
const MEASURE_ALLOWANCE = 2;

let measuringContext = null;

/**
 * The width, in pixels, of the longest sheet name and the longest answer in these submissions, drawn in the given
 * font. An answer over several lines is as wide as its longest line.
 */
function measureColumns(submissions, question, catalog, font) {
  if (!measuringContext) {
    measuringContext = document.createElement('canvas').getContext('2d');
  }

  measuringContext.font = font;
  const measure = text => measuringContext.measureText(text).width;
  const catalogMap = isCatalogQuestion(question) ? buildCatalogMap(catalog) : null;
  const imageAnswers = question.type === 'IMAGE' || question.type === 'SIGNATURE';

  const widths = submissions.reduce((widest, submission) => {
    const sheet = measure(submission.sheetName || 'Unnamed sheet');
    const answer = imageAnswers
      ? IMAGE_ANSWER_WIDTH
      : String(getAnswerText(question, submission.answers, catalogMap))
        .split('\n')
        .reduce((width, line) => Math.max(width, measure(line)), 0);

    return {
      sheet: Math.max(widest.sheet, sheet),
      answer: Math.max(widest.answer, answer)
    };
  }, {sheet: 0, answer: 0});

  return {
    sheet: Math.ceil(widths.sheet) + MEASURE_ALLOWANCE,
    answer: Math.ceil(widths.answer) + MEASURE_ALLOWANCE
  };
}

function toMap(submissions) {
  return submissions.reduce((map, submission) => {
    map[submission.rowId] = submission;
    return map;
  }, {});
}

const highlight = keyframes`
  from { background: rgba(0, 73, 238, 0.1); }
  to { background: transparent; }
`;

const SubTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
  padding: 0 1.875rem 0.75rem 1.875rem;
  margin-top: -0.75rem;
`;

const QuestionText = styled.div`
  color: #747474;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Status = styled.div`
  font-size: 0.85rem;
  white-space: nowrap;
  color: ${props => props.failed ? '#D32F2F' : '#747474'};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1.875rem;
`;

const HeaderRow = styled(Row)`
  font-weight: 500;
  color: #747474;
  font-size: 0.85rem;
  text-transform: uppercase;
  border-bottom: 1px solid #E4E4E4;
`;

const SubmissionRow = styled(Row)`
  border-bottom: 1px solid #E4E4E4;
  min-height: 3.5rem;

  ${props => props.highlighted && css`
    animation: ${highlight} ${HIGHLIGHT_DURATION}ms ease-out;
  `}
`;

const DateColumn = styled.div`
  width: 8rem;
  flex-shrink: 0;

  a, a:visited {
    color: #747474;
  }
`;


// The sheet and answer columns share the room the others leave. Each starts at the width of its longest entry and,
// when there is not room for both, gives way in proportion to it - never below its minimum. Any room over goes to the
// answer, which can wrap. The sheet column grows no wider than its longest name.
const SheetColumn = styled.div`
  flex: 0 1 var(--sheet-width, 6rem);
  min-width: 6rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubmitterColumn = styled.div`
  width: 9rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  min-width: 0;
`;

const SubmitterName = styled.div`
  margin-left: 0.75rem;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AnswerColumn = styled.div`
  flex: 1 1 var(--answer-width, 0);
  min-width: 4rem;
  overflow-wrap: anywhere;
`;

const ScrollContent = styled.div`
  height: 36rem;
`;

const Container = styled.div`
  padding: 0 1.875rem 1.875rem 1.875rem;
  height: 85%;
`;

export default withRouter(React.memo(RecentSubmissionsWidget));
